import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, PieChart, Pie,
  LineChart, Line, Tooltip,
} from "recharts";
import { ShieldWarning, TrendUp, Users, Fire, ArrowRight } from "@phosphor-icons/react";
import HelpTip from "@/components/onboarding/HelpTip";

const Stat = ({ label, value, icon: Icon, accent, testid, help }) => (
  <div data-testid={testid} className="border p-5 bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider flex items-center" style={{ color: "var(--text-2)" }}>{label}{help && <HelpTip text={help} />}</span>
      <Icon size={18} weight="duotone" color={accent} />
    </div>
    <div className="mono text-3xl font-bold mt-3" style={{ color: accent }}>{value}</div>
  </div>
);

export default function Dashboard() {
  const [report, setReport] = useState(null);
  const [audits, setAudits] = useState([]);
  const [trend, setTrend] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.report().then((r) => setReport(r.data)).catch(() => {});
    api.listAudits().then((r) => setAudits(r.data)).catch(() => {});
    api.auditTrend().then((r) => setTrend(r.data?.rows || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const lastAudit = audits[0];
  const colors = ["#3B82F6", "#00F0FF", "#FACC15", "#10B981", "#22c55e", "#EF4444"];

  return (
    <div className="fade-up space-y-8 max-w-6xl">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat testid="stat-seo-score" label="Avg SEO Score" value={lastAudit ? lastAudit.avg_score : "—"} icon={TrendUp} accent="var(--accent)" help="Your website's average technical SEO score (0-100) from the last audit you ran. Go to SEO Audit to run or refresh it." />
        <Stat testid="stat-critical" label="Critical Issues" value={lastAudit ? lastAudit.critical_issues : "—"} icon={ShieldWarning} accent="var(--danger)" help="Serious technical problems found in your last audit -- fix these first, they usually matter most for rankings." />
        <Stat testid="stat-leads" label="Pipeline Leads" value={report ? report.total : "—"} icon={Users} accent="var(--primary)" help="Total leads currently sitting in your Conversion pipeline, across every stage." />
        <Stat testid="stat-hot" label="Hot Leads" value={report ? report.hot : "—"} icon={Fire} accent="var(--warning)" help="Leads the AI scored highly (75+) -- your best opportunities to follow up on right now." />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border p-6 bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-bold">Pipeline by Stage</h2>
            <button data-testid="go-conversion" onClick={() => nav("/conversion")} className="text-xs flex items-center gap-1 hover:text-white transition-colors" style={{ color: "var(--accent)" }}>
              View pipeline <ArrowRight size={13} />
            </button>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={report?.by_stage || []}>
              <XAxis dataKey="stage" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={{ stroke: "#1f2937" }} tickLine={false} />
              <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                {(report?.by_stage || []).map((entry, i) => <Cell key={entry.stage} fill={colors[i % colors.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border p-6 bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
          <h2 className="text-base font-bold mb-5">Deal Interest</h2>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={report?.interest || []} dataKey="count" nameKey="type" innerRadius={45} outerRadius={80} paddingAngle={3}>
                <Cell fill="#3B82F6" />
                <Cell fill="#00F0FF" />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-5 text-xs mono">
            <span style={{ color: "#3B82F6" }}>■ Monthly</span>
            <span style={{ color: "#00F0FF" }}>■ License</span>
          </div>
        </div>
      </div>

      {trend.length >= 2 && (
        <div className="border p-6 bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
          <h2 className="text-base font-bold mb-5">SEO Score Trend</h2>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trend.map((t) => ({ ...t, label: new Date(t.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) }))}>
              <XAxis dataKey="label" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={{ stroke: "#1f2937" }} tickLine={false} />
              <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} domain={[0, 100]} />
              <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--line)", fontSize: 12 }} labelStyle={{ color: "#9CA3AF" }} />
              <Line type="monotone" dataKey="avg_score" stroke="var(--accent)" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="border p-6 bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold">Recent Audits</h2>
          <button data-testid="go-audit" onClick={() => nav("/seo-audit")} className="text-xs flex items-center gap-1 hover:text-white transition-colors" style={{ color: "var(--accent)" }}>
            Run new audit <ArrowRight size={13} />
          </button>
        </div>
        {audits.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--text-2)" }}>No audits yet. Run your first crawl to see technical SEO scores.</p>
        ) : (
          <div className="space-y-2">
            {audits.slice(0, 5).map((a) => (
              <div key={a.id} onClick={() => nav("/seo-audit")} className="flex items-center justify-between border px-4 py-3 cursor-pointer hover:bg-[#0e1626] transition-colors" style={{ borderColor: "var(--line)" }}>
                <span className="mono text-sm truncate">{a.url}</span>
                <div className="flex items-center gap-6 mono text-xs">
                  <span style={{ color: "var(--text-2)" }}>{a.pages_count} pages</span>
                  <span style={{ color: a.avg_score > 70 ? "var(--success)" : "var(--warning)" }}>score {a.avg_score}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
